(() => {
  const ENDPOINT = '/apps/aotm-collection-results';
  const DEBOUNCE_MS = 500;
  const OBSERVER_DEBOUNCE_MS = 80;
  const MAX_CONSECUTIVE_FAILURES = 2;
  const ROOT_SELECTOR = '[data-aotm-collection-grid-root]';
  const SEARCH_SELECTOR = '[data-aotm-collection-search-input]';
  const SOLD_TOGGLE_SELECTOR = '[data-aotm-collection-sold-last-toggle]';
  const SEARCH_CLEAR_SELECTOR = '[data-aotm-collection-search-clear]';
  const PRE_SEARCH_SORT_DEFAULT = '__AOTM_DEFAULT_SORT__';
  const SCROLL_TO_GRID_AFTER_CLEAR_KEY = 'aotm-scroll-to-grid-after-search-clear';

  class AotmCollectionResultsController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.collectionHandle = root.dataset.collectionHandle || '';
      this.perPage = Number(root.dataset.productsPerPage || '24');

      this.abortController = null;
      this.debounceTimer = null;
      this.observerTimer = null;
      this.mutationObserver = null;
      this.isApplyingProxyRender = false;
      this.consecutiveFailures = 0;
      this.preSearchSortBy = null;
      this.wasSearchActive = false;
      this.requestSequence = 0;
      this.activeRequestSequence = 0;
      this.forceDefaultSortOnNextUrl = false;
      this.isNativeRefreshInProgress = false;

      this.searchInput = null;
      this.searchClearButton = null;
      this.soldLastToggle = null;
      this.grid = null;
      this.productCountDesktop = null;
      this.productCountMobile = null;
      this.pageStatus = null;
      this.paginationWrapper = null;

      this.originalGridHtml = '';
      this.originalPaginationHtml = '';
      this.originalDesktopCountText = '';
      this.originalMobileCountText = '';
      this.originalPageStatusText = '';
      this.originalCardMarkupByHandle = new Map();
      this.originalImageFallbackByHandle = new Map();

      this.refreshDomReferences();

      if (!this.collectionHandle || !this.grid) {
        console.error('AOTM v12 controller: missing collection handle or grid');
        this.clearInitialPendingState();
        return;
      }

      this.captureOriginalState();
      this.syncControlsFromUrl();
      this.syncFacetVisibility();
      this.updateSearchClearButtonVisibility();
      this.hydrateProductImages();
      this.bindEvents();
      this.bindNativeFilterAndSortPersistence();
      this.observeBaseGridChanges();

      this.root.dataset.aotmCollectionControllerReady = 'true';
      console.log('AOTM v12 controller initialised');

      if (this.isEnhancementActive()) {
        window.requestAnimationFrame(() => {
          this.loadResults({ syncOnly: true });
        });
      } else {
        this.clearInitialPendingState();
      }

      this.scrollToGridAfterSearchClearRefresh();
    }

    refreshDomReferences() {
      this.searchInput = this.root.querySelector(SEARCH_SELECTOR);
      this.searchClearButton = this.root.querySelector(SEARCH_CLEAR_SELECTOR);
      this.soldLastToggle = this.root.querySelector(SOLD_TOGGLE_SELECTOR);
      this.grid = this.root.querySelector('#product-grid');
      this.productCountDesktop = this.root.querySelector('#ProductCountDesktop');
      this.productCountMobile = this.root.querySelector('#ProductCount');
      this.pageStatus = this.root.querySelector('.aotm-collection-toolbar__status-text');
      this.paginationWrapper = this.root.querySelector('.pagination-wrapper');
    }

    getSortFields() {
      return [
        this.root.querySelector('select[name="sort_by"]'),
        document.querySelector('select[name="sort_by"]'),
      ].filter(Boolean);
    }

    isSearchActive() {
      this.refreshDomReferences();
      return Boolean(this.searchInput && this.searchInput.value.trim() !== '');
    }

    getPreSearchSortStorageKey() {
      return `aotm-pre-search-sort:${this.collectionHandle || 'collection'}`;
    }

    readStoredPreSearchSort() {
      try {
        return window.sessionStorage.getItem(this.getPreSearchSortStorageKey()) || null;
      } catch (_) {
        return null;
      }
    }

    writeStoredPreSearchSort(value) {
      try {
        window.sessionStorage.setItem(this.getPreSearchSortStorageKey(), value);
      } catch (_) {}
    }

    clearStoredPreSearchSort() {
      try {
        window.sessionStorage.removeItem(this.getPreSearchSortStorageKey());
      } catch (_) {}
    }

    getSortFromUrlOrField() {
      const params = new URLSearchParams(window.location.search);
      const urlSort = params.get('sort_by') || '';

      if (urlSort && urlSort !== 'most-relevant') {
        return urlSort;
      }

      const sortField = this.getSortFields().find((field) => field.value);
      const fieldSort = sortField && sortField.value ? sortField.value : '';

      if (fieldSort && fieldSort !== 'most-relevant') {
        return fieldSort;
      }

      return '';
    }

    storePreSearchSort() {
      if (this.preSearchSortBy !== null) return;

      const currentSort = this.getSortFromUrlOrField();
      const sortToStore = currentSort || PRE_SEARCH_SORT_DEFAULT;

      this.preSearchSortBy = sortToStore;
      this.writeStoredPreSearchSort(sortToStore);
    }

    setSortToMostRelevantForSearch() {
      this.getSortFields().forEach((field) => {
        if (field.value !== 'most-relevant') {
          field.value = 'most-relevant';
        }
      });
    }

    getSortToRestoreAfterSearch() {
      return (
        this.preSearchSortBy ||
        this.readStoredPreSearchSort() ||
        PRE_SEARCH_SORT_DEFAULT
      );
    }

    restorePreSearchSort() {
      const sortToRestore = this.getSortToRestoreAfterSearch();

      if (sortToRestore === PRE_SEARCH_SORT_DEFAULT) {
        this.forceDefaultSortOnNextUrl = true;
      } else {
        this.forceDefaultSortOnNextUrl = false;

        this.getSortFields().forEach((field) => {
          const hasOption = Array.from(field.options || []).some(
            (option) => option.value === sortToRestore
          );

          if (hasOption) {
            field.value = sortToRestore;
          }
        });
      }

      this.preSearchSortBy = null;
      this.clearStoredPreSearchSort();
    }

    rememberGridScrollAfterSearchClear() {
      try {
        const targetId = this.sectionId
          ? `CollectionGridTop-${this.sectionId}`
          : 'ProductGridContainer';

        window.sessionStorage.setItem(SCROLL_TO_GRID_AFTER_CLEAR_KEY, targetId);
      } catch (_) {}
    }

    scrollToGridAfterSearchClearRefresh() {
      let targetId = '';

      try {
        targetId = window.sessionStorage.getItem(SCROLL_TO_GRID_AFTER_CLEAR_KEY) || '';
        window.sessionStorage.removeItem(SCROLL_TO_GRID_AFTER_CLEAR_KEY);
      } catch (_) {}

      if (!targetId && window.location.hash) {
        targetId = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      }

      if (!targetId) return;

      const scroll = () => {
        const target =
          document.getElementById(targetId) ||
          this.root.querySelector(`#${CSS.escape(targetId)}`) ||
          this.root.querySelector('#ProductGridContainer');

        if (!target) return;

        target.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
      };

      window.requestAnimationFrame(() => {
        window.setTimeout(scroll, 120);
      });
    }

    buildNativeUrlAfterSearchClear(sortToRestore) {
      const params = new URLSearchParams(window.location.search);

      params.delete('cq');
      params.delete('page');

      if (sortToRestore && sortToRestore !== PRE_SEARCH_SORT_DEFAULT) {
        params.set('sort_by', sortToRestore);
      } else {
        params.delete('sort_by');
      }

      params.set('sold_last', this.isSoldLastActive() ? '1' : '0');

      const queryString = params.toString();
      const gridAnchor = this.sectionId
        ? `#CollectionGridTop-${encodeURIComponent(this.sectionId)}`
        : '#ProductGridContainer';

      return `${window.location.pathname}${queryString ? `?${queryString}` : ''}${gridAnchor}`;
    }

    nativeRefreshAfterSearchClear() {
      if (this.isNativeRefreshInProgress) return;

      this.isNativeRefreshInProgress = true;
      this.cancelPendingProxyWork();

      const sortToRestore = this.getSortToRestoreAfterSearch();
      const nextUrl = this.buildNativeUrlAfterSearchClear(sortToRestore);

      this.preSearchSortBy = null;
      this.forceDefaultSortOnNextUrl = sortToRestore === PRE_SEARCH_SORT_DEFAULT;
      this.clearStoredPreSearchSort();
      this.rememberGridScrollAfterSearchClear();

      window.location.assign(nextUrl);
    }

    getImageFallbackFromElement(img) {
      if (!img) return null;

      return {
        src:
          img.getAttribute('src') ||
          img.dataset.src ||
          img.dataset.originalSrc ||
          '',
        srcset:
          img.getAttribute('srcset') ||
          img.dataset.srcset ||
          img.dataset.originalSrcset ||
          '',
        sizes:
          img.getAttribute('sizes') ||
          img.dataset.sizes ||
          '',
        alt:
          img.getAttribute('alt') ||
          '',
      };
    }

    getCardHandle(card) {
      if (!card) return '';

      const productLink = card.querySelector('a[href*="/products/"]');
      if (!productLink) return '';

      return this.extractHandleFromHref(productLink.getAttribute('href'));
    }

    seedImageFallbacksFromProducts(products) {
      if (!Array.isArray(products)) return;

      products.forEach((product) => {
        if (!product?.handle || !product?.image?.url) return;

        this.originalImageFallbackByHandle.set(product.handle, {
          src: product.image.url,
          srcset: '',
          sizes: '',
          alt: product.image.altText || product.title || '',
        });
      });
    }

    patchCachedCardImageMarkup(markup, product, index) {
      if (!markup || !product?.image?.url) return markup;

      const wrapper = document.createElement('div');
      wrapper.innerHTML = markup;

      const img = wrapper.querySelector('img');

      if (img) {
        img.removeAttribute('srcset');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
        img.removeAttribute('data-original-srcset');
        img.removeAttribute('data-original-src');
        img.removeAttribute('sizes');

        img.setAttribute('src', product.image.url);
        img.setAttribute('alt', product.image.altText || product.title || '');
        img.setAttribute('loading', index < 6 ? 'eager' : 'lazy');
        img.setAttribute('decoding', 'async');

        if (index < 3) {
          img.setAttribute('fetchpriority', 'high');
        } else {
          img.removeAttribute('fetchpriority');
        }
      }

      return wrapper.innerHTML;
    }

    recoverImageFromFallback(img, handle) {
      if (!img || !handle) return;

      const fallback = this.originalImageFallbackByHandle.get(handle);
      if (!fallback) return;

      const currentSrc = img.getAttribute('src') || img.currentSrc || '';
      const hasUsableSrc = Boolean(currentSrc);
      const hasUsableSrcset = Boolean(img.getAttribute('srcset'));

      if (!hasUsableSrc && fallback.src) {
        img.setAttribute('src', fallback.src);
      }

      if (!hasUsableSrcset && fallback.srcset) {
        img.setAttribute('srcset', fallback.srcset);
      }

      if (!img.getAttribute('sizes') && fallback.sizes) {
        img.setAttribute('sizes', fallback.sizes);
      }

      if (!img.getAttribute('alt') && fallback.alt) {
        img.setAttribute('alt', fallback.alt);
      }

      if (fallback.src && !img.dataset.aotmFallbackBound) {
        img.dataset.aotmFallbackBound = 'true';

        img.addEventListener('error', () => {
          img.removeAttribute('srcset');
          img.setAttribute('src', fallback.src);
        });

        window.setTimeout(() => {
          if (!img.complete) {
            img.removeAttribute('srcset');
            img.setAttribute('src', fallback.src);
          }
        }, 900);
      }
    }

    hydrateProductImages({ delayed = true } = {}) {
      this.refreshDomReferences();
      if (!this.grid) return;

      const cards = Array.from(this.grid.querySelectorAll('.aotm-collection-grid__item'));

      cards.forEach((card, index) => {
        const img = card.querySelector('img');
        if (!img) return;

        const handle = this.getCardHandle(card);
        const isPriorityImage = index < 6;
        const isVeryHighPriorityImage = index < 3;

        if (isPriorityImage) {
          img.loading = 'eager';
          img.decoding = 'async';
        }

        if (isVeryHighPriorityImage) {
          try {
            img.fetchPriority = 'high';
          } catch (_) {
            img.setAttribute('fetchpriority', 'high');
          }
        }

        if (img.dataset.src && !img.getAttribute('src')) {
          img.setAttribute('src', img.dataset.src);
        }

        if (img.dataset.srcset && !img.getAttribute('srcset')) {
          img.setAttribute('srcset', img.dataset.srcset);
        }

        if (img.dataset.sizes && !img.getAttribute('sizes')) {
          img.setAttribute('sizes', img.dataset.sizes);
        }

        this.recoverImageFromFallback(img, handle);
      });

      if (delayed) {
        window.setTimeout(() => {
          this.hydrateProductImages({ delayed: false });
        }, 220);
      }
    }

    showSearchUnavailableNotice(message = 'Collection search is temporarily unavailable. Please use the filters or browse the collection.') {
      this.refreshDomReferences();

      let notice = this.root.querySelector('[data-aotm-search-unavailable-notice]');

      if (!notice) {
        notice = document.createElement('p');
        notice.setAttribute('data-aotm-search-unavailable-notice', 'true');

        Object.assign(notice.style, {
          display: 'none',
          margin: '0.5rem 0 0',
          fontSize: '1.16rem',
          lineHeight: '1.45',
          letterSpacing: '0',
          color: 'rgba(var(--color-foreground), 0.72)',
          width: '100%',
          flexBasis: '100%',
        });

        const enhancements = this.root.querySelector('[data-aotm-collection-enhancements]');
        const controls = enhancements ? enhancements.querySelector('.aotm-collection-enhancements__controls') : null;

        if (controls) {
          controls.insertAdjacentElement('afterend', notice);
        } else if (enhancements) {
          enhancements.appendChild(notice);
        }
      }

      notice.textContent = message;
      notice.style.display = 'block';
      notice.setAttribute('aria-hidden', 'false');
    }

    hideSearchUnavailableNotice() {
      const notice = this.root.querySelector('[data-aotm-search-unavailable-notice]');
      if (!notice) return;

      notice.style.display = 'none';
      notice.setAttribute('aria-hidden', 'true');
    }

    disableEnhancementControls() {
      this.refreshDomReferences();

      if (this.searchInput) {
        this.searchInput.disabled = true;
        this.searchInput.setAttribute('aria-disabled', 'true');
      }

      if (this.searchClearButton) {
        this.searchClearButton.disabled = true;
      }

      if (this.soldLastToggle) {
        this.soldLastToggle.disabled = true;
        this.soldLastToggle.setAttribute('aria-disabled', 'true');
      }

      this.root.setAttribute('data-aotm-search-unavailable', 'true');
    }

    updateSearchClearButtonVisibility() {
      this.refreshDomReferences();

      if (!this.searchInput || !this.searchClearButton) return;

      const hasValue = this.searchInput.value.length > 0;

      this.searchClearButton.hidden = !hasValue;
      this.searchClearButton.disabled = !hasValue || this.searchInput.disabled;
      this.searchClearButton.setAttribute('aria-hidden', hasValue ? 'false' : 'true');

      const wrapper =
        this.searchInput.closest('[data-aotm-collection-search-field], .aotm-collection-search-field') ||
        this.searchInput.parentElement;

      if (wrapper) {
        wrapper.toggleAttribute('data-has-value', hasValue);
      }
    }

    clearSearch({ preserveFocus = true } = {}) {
      this.refreshDomReferences();

      if (!this.searchInput || this.searchInput.disabled) return;

      this.searchInput.value = '';
      this.handleSearchCleared({ preserveSearchFocus: preserveFocus });

      if (preserveFocus && document.activeElement !== this.searchInput && !this.isNativeRefreshInProgress) {
        this.searchInput.focus({ preventScroll: true });
      }
    }

    resetLoadingVisualState() {
      this.root.removeAttribute('data-aotm-loading');
      this.root.removeAttribute('data-aotm-hydrating');
      document.documentElement.removeAttribute('data-aotm-collection-pending');

      this.refreshDomReferences();

      if (this.grid) {
        this.grid.style.opacity = '';
        this.grid.style.transition = '';
      }

      if (this.soldLastToggle && !this.root.hasAttribute('data-aotm-search-unavailable')) {
        this.soldLastToggle.disabled = false;
      }
    }

    cancelPendingProxyWork() {
      window.clearTimeout(this.debounceTimer);

      this.requestSequence += 1;
      this.activeRequestSequence = this.requestSequence;

      if (this.abortController) {
        try {
          this.abortController.abort();
        } catch (_) {}

        this.abortController = null;
      }

      this.resetLoadingVisualState();
    }

    handleSearchCleared({ preserveSearchFocus = true } = {}) {
      this.updateSearchClearButtonVisibility();
      this.syncFacetVisibility();
      this.hideSearchUnavailableNotice();

      const hadSearchState =
        this.wasSearchActive ||
        Boolean(this.readStoredPreSearchSort()) ||
        new URLSearchParams(window.location.search).has('cq');

      if (hadSearchState) {
        this.nativeRefreshAfterSearchClear();
        return;
      }

      this.cancelPendingProxyWork();
      this.restorePreSearchSort();

      if (this.isEnhancementActive()) {
        this.loadResults({
          resetPage: true,
          pushUrl: true,
          preserveSearchFocus,
        });
      } else {
        this.restoreOriginalState({
          updateUrl: true,
          restoreSearchFocus: preserveSearchFocus,
        });
      }

      this.wasSearchActive = false;
    }

    clearInitialPendingState() {
      document.documentElement.removeAttribute('data-aotm-collection-pending');
      this.root.removeAttribute('data-aotm-hydrating');
    }

    captureOriginalState() {
      this.refreshDomReferences();
      if (!this.grid) return;

      this.originalGridHtml = this.grid.innerHTML;
      this.originalPaginationHtml = this.paginationWrapper ? this.paginationWrapper.outerHTML : '';
      this.originalDesktopCountText = this.productCountDesktop ? this.productCountDesktop.textContent : '';
      this.originalMobileCountText = this.productCountMobile ? this.productCountMobile.textContent : '';
      this.originalPageStatusText = this.pageStatus ? this.pageStatus.textContent : '';

      this.originalCardMarkupByHandle = new Map();
      this.originalImageFallbackByHandle = new Map();

      this.grid.querySelectorAll('.aotm-collection-grid__item').forEach((item) => {
        const handle = this.getCardHandle(item);
        if (!handle) return;

        this.originalCardMarkupByHandle.set(handle, item.innerHTML);

        const img = item.querySelector('img');
        const fallback = this.getImageFallbackFromElement(img);

        if (fallback && (fallback.src || fallback.srcset)) {
          this.originalImageFallbackByHandle.set(handle, fallback);
        }
      });
    }

    observeBaseGridChanges() {
      this.mutationObserver = new MutationObserver(() => {
        if (this.isApplyingProxyRender) return;
        if (this.root.hasAttribute('data-aotm-loading')) return;
        if (this.isNativeRefreshInProgress) return;

        window.clearTimeout(this.observerTimer);

        this.observerTimer = window.setTimeout(() => {
          this.refreshDomReferences();
          if (!this.grid) return;

          this.updateSearchClearButtonVisibility();
          this.syncFacetVisibility();
          this.hydrateProductImages();

          if (this.isEnhancementActive()) {
            this.loadResults({ syncOnly: true, resetPage: true });
            return;
          }

          this.captureOriginalState();
        }, OBSERVER_DEBOUNCE_MS);
      });

      this.mutationObserver.observe(this.root, {
        childList: true,
        subtree: true,
      });
    }

    extractHandleFromHref(href) {
      if (!href) return '';

      try {
        const parsed = new URL(href, window.location.origin);
        const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      } catch {
        const match = href.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      }
    }

    isHandleSold(handle) {
      const markup = this.originalCardMarkupByHandle.get(handle) || '';

      return (
        /aotm-product-card__status/i.test(markup) ||
        />\s*SOLD\s*</i.test(markup) ||
        />\s*Sold\s*</i.test(markup)
      );
    }

    syncControlsFromUrl() {
      this.refreshDomReferences();
      const params = new URLSearchParams(window.location.search);

      if (this.searchInput) {
        this.searchInput.value = params.get('cq') || '';
      }

      if (this.soldLastToggle) {
        this.soldLastToggle.checked = params.get('sold_last') === '1';
      }

      this.wasSearchActive = this.isSearchActive();

      if (this.wasSearchActive && this.preSearchSortBy === null) {
        this.preSearchSortBy = this.readStoredPreSearchSort();
      }

      if (!this.wasSearchActive) {
        this.preSearchSortBy = null;
        this.forceDefaultSortOnNextUrl = false;
      }

      this.updateSearchClearButtonVisibility();
    }

    syncFacetVisibility() {
      this.refreshDomReferences();

      const filtersRoot = this.root.querySelector('#main-collection-filters');
      if (!filtersRoot) return;

      const searchActive = Boolean(this.searchInput && this.searchInput.value.trim() !== '');
      const facetBlocks = filtersRoot.querySelectorAll('.js-filter');

      facetBlocks.forEach((block) => {
        const isPriceFacet =
          block.querySelector('input[name="filter.v.price.gte"]') ||
          block.querySelector('input[name="filter.v.price.lte"]') ||
          block.querySelector('[href*="filter.v.price.gte"]') ||
          block.querySelector('[href*="filter.v.price.lte"]');

        if (!isPriceFacet) {
          block.hidden = searchActive;
          block.setAttribute('aria-hidden', searchActive ? 'true' : 'false');
        }
      });

      filtersRoot.toggleAttribute('data-aotm-search-active', searchActive);

      let note = this.root.querySelector('[data-aotm-search-facet-note]');
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-aotm-search-facet-note', 'true');
        note.textContent = 'Clear search to use all collection filters.';

        Object.assign(note.style, {
          display: 'none',
          margin: '0.5rem 0 0',
          fontSize: '1.16rem',
          lineHeight: '1.45',
          letterSpacing: '0',
          color: 'rgba(var(--color-foreground), 0.72)',
          width: '100%',
          flexBasis: '100%',
        });

        const enhancements = this.root.querySelector('[data-aotm-collection-enhancements]');
        const controls = enhancements ? enhancements.querySelector('.aotm-collection-enhancements__controls') : null;

        if (controls) {
          controls.insertAdjacentElement('afterend', note);
        } else if (enhancements) {
          enhancements.appendChild(note);
        } else {
          filtersRoot.insertAdjacentElement('beforebegin', note);
        }
      }

      note.style.display = searchActive ? 'block' : 'none';
      note.setAttribute('aria-hidden', searchActive ? 'false' : 'true');
    }

    shouldRunSearch() {
      this.refreshDomReferences();

      if (!this.searchInput || this.searchInput.disabled) return false;

      const query = this.searchInput.value.trim();
      return query.length === 0 || query.length >= 2;
    }

    isEnhancementActive() {
      this.refreshDomReferences();

      const searchActive = this.searchInput && this.searchInput.value.trim() !== '';
      const soldLastActive = this.isSoldLastActive();

      return Boolean(searchActive || soldLastActive);
    }

    getCurrentParams() {
      this.refreshDomReferences();

      const params = new URLSearchParams(window.location.search);

      const sortField =
        this.root.querySelector('select[name="sort_by"]') ||
        document.querySelector('select[name="sort_by"]');

      if (this.forceDefaultSortOnNextUrl) {
        params.delete('sort_by');
      } else if (sortField && sortField.value) {
        params.set('sort_by', sortField.value);
      }

      if (this.searchInput) {
        const query = this.searchInput.value.trim();
        if (query) {
          params.set('cq', query);
        } else {
          params.delete('cq');
        }
      }

      params.set('sold_last', this.isSoldLastActive() ? '1' : '0');

      return params;
    }

    updateBrowserUrl({ page = null, resetPage = false } = {}) {
      const params = this.getCurrentParams();

      if (this.isEnhancementActive()) {
        if (resetPage) {
          params.set('page', '1');
        } else if (page) {
          params.set('page', String(page));
        }
      } else {
        params.delete('page');
      }

      const queryString = params.toString();
      const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

      window.history.replaceState({}, '', nextUrl);
      console.log('AOTM v12 URL updated:', nextUrl);
    }

    buildRequestUrl({ page = null, resetPage = false } = {}) {
      const params = this.getCurrentParams();

      params.set('collection', this.collectionHandle);
      params.set('per_page', String(this.perPage));

      if (resetPage) {
        params.set('page', '1');
      } else if (page) {
        params.set('page', String(page));
      } else if (!params.get('page')) {
        params.set('page', '1');
      }

      return `${ENDPOINT}?${params.toString()}`;
    }

    captureSearchFieldState() {
      this.refreshDomReferences();

      if (!this.searchInput) {
        return {
          shouldRestoreFocus: false,
          selectionStart: null,
          selectionEnd: null,
          value: '',
        };
      }

      return {
        shouldRestoreFocus: document.activeElement === this.searchInput,
        selectionStart:
          typeof this.searchInput.selectionStart === 'number'
            ? this.searchInput.selectionStart
            : null,
        selectionEnd:
          typeof this.searchInput.selectionEnd === 'number'
            ? this.searchInput.selectionEnd
            : null,
        value: this.searchInput.value,
      };
    }

    restoreSearchFieldState(state) {
      this.refreshDomReferences();

      if (!this.searchInput || !state?.shouldRestoreFocus) return;

      const valueUnchanged = this.searchInput.value === state.value;
      if (!valueUnchanged) return;

      if (document.activeElement !== this.searchInput) {
        this.searchInput.focus({ preventScroll: true });
      }

      if (
        typeof state.selectionStart === 'number' &&
        typeof state.selectionEnd === 'number'
      ) {
        const max = this.searchInput.value.length;
        const start = Math.max(0, Math.min(state.selectionStart, max));
        const end = Math.max(start, Math.min(state.selectionEnd, max));

        window.requestAnimationFrame(() => {
          try {
            this.searchInput.setSelectionRange(start, end);
          } catch (_) {}
        });
      }
    }

    restoreOriginalState({ updateUrl = false, restoreSearchFocus = false } = {}) {
      const searchFieldState = restoreSearchFocus ? this.captureSearchFieldState() : null;

      this.refreshDomReferences();
      if (!this.grid) return;

      this.isApplyingProxyRender = true;

      this.grid.innerHTML = this.originalGridHtml;
      this.refreshDomReferences();

      if (this.paginationWrapper) {
        this.paginationWrapper.remove();
        this.paginationWrapper = null;
      }

      if (this.originalPaginationHtml && this.grid) {
        this.grid.insertAdjacentHTML('afterend', this.originalPaginationHtml);
        this.refreshDomReferences();
      }

      if (this.productCountDesktop) {
        this.productCountDesktop.textContent = this.originalDesktopCountText;
      }

      if (this.productCountMobile) {
        this.productCountMobile.textContent = this.originalMobileCountText;
      }

      if (this.pageStatus) {
        this.pageStatus.textContent = this.originalPageStatusText;
      }

      this.hydrateProductImages();

      if (updateUrl) {
        this.updateBrowserUrl({ resetPage: true });
      }

      if (restoreSearchFocus) {
        this.restoreSearchFieldState(searchFieldState);
      }

      this.updateSearchClearButtonVisibility();
      this.syncFacetVisibility();
      this.clearInitialPendingState();
      this.resetLoadingVisualState();

      window.setTimeout(() => {
        this.isApplyingProxyRender = false;
      }, 0);
    }

    setLoadingState(isLoading, { hydrating = false } = {}) {
      this.root.toggleAttribute('data-aotm-loading', isLoading);

      if (isLoading && hydrating) {
        this.root.setAttribute('data-aotm-hydrating', 'true');
      } else if (!isLoading) {
        this.root.removeAttribute('data-aotm-hydrating');
      }

      this.refreshDomReferences();

      if (this.grid) {
        if (isLoading && !hydrating) {
          this.grid.style.opacity = '0.45';
          this.grid.style.transition = 'opacity 0.18s ease';
        } else {
          this.grid.style.opacity = '';
          this.grid.style.transition = '';
        }
      }

      if (this.soldLastToggle && !this.root.hasAttribute('data-aotm-search-unavailable')) {
        this.soldLastToggle.disabled = isLoading;
      }
    }

    getPersistentCustomParams() {
      this.refreshDomReferences();

      const params = new URLSearchParams();

      if (this.searchInput) {
        const query = this.searchInput.value.trim();
        if (query) {
          params.set('cq', query);
        }
      }

      params.set('sold_last', this.isSoldLastActive() ? '1' : '0');

      return params;
    }

    appendCustomParamsToForm(form) {
      if (!form) return;

      form.querySelectorAll('[data-aotm-custom-param]').forEach((input) => input.remove());

      const customParams = this.getPersistentCustomParams();

      for (const [key, value] of customParams.entries()) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        input.setAttribute('data-aotm-custom-param', 'true');
        form.appendChild(input);
      }
    }

    bindNativeFilterAndSortPersistence() {
      this.root.addEventListener(
        'submit',
        (event) => {
          const form = event.target.closest('form');
          if (!form) return;
          this.appendCustomParamsToForm(form);
        },
        true
      );

      this.root.addEventListener(
        'change',
        (event) => {
          const target = event.target;
          const form = target && target.closest ? target.closest('form') : null;
          if (!form) return;
          this.appendCustomParamsToForm(form);
        },
        true
      );
    }

    productCountLabel(count) {
      return `${count} product${count === 1 ? '' : 's'}`;
    }

    updateStatusLabels(data) {
      this.refreshDomReferences();

      const resultCount = Number(data.total_results || 0);
      const countLabel = this.productCountLabel(resultCount);
      const pageLabel =
        resultCount > 0
          ? `Page ${data.current_page} of ${data.total_pages}`
          : 'No matching products';

      if (this.productCountDesktop) {
        this.productCountDesktop.textContent = countLabel;
      }

      if (this.productCountMobile) {
        this.productCountMobile.textContent = countLabel;
      }

      if (this.pageStatus) {
        this.pageStatus.textContent = pageLabel;
      }
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }

    formatPrice(price) {
      if (price === null || price === undefined || price === '') return '';
      const num = Number(price);
      if (Number.isNaN(num)) return this.escapeHtml(price);
      return `£${num.toFixed(2)}`;
    }

    renderFallbackCardMarkup(product, index) {
      const title = this.escapeHtml(product.title || '');
      const handle = this.escapeHtml(product.handle || '');
      const url = `/products/${handle}`;
      const isSoldOut = !product.availableForSale;
      const imageUrl = product.image?.url ? this.escapeHtml(product.image.url) : '';
      const imageAlt = this.escapeHtml(product.image?.altText || product.title || '');
      const price = this.formatPrice(product.price);

      return `
        <div
          class="aotm-collection-grid__item"
          ${index === 0 ? `id="CollectionGridTop-${this.sectionId}"` : ''}
        >
          <a class="aotm-product-card ${isSoldOut ? 'aotm-product-card--sold-out' : ''}" href="${url}">
            <div class="aotm-product-card__image-wrap">
              ${isSoldOut ? '<span class="aotm-product-card__status">SOLD</span>' : ''}
              ${
                imageUrl
                  ? `<img class="aotm-product-card__image" src="${imageUrl}" alt="${imageAlt}" loading="${index < 6 ? 'eager' : 'lazy'}" decoding="async" ${index < 3 ? 'fetchpriority="high"' : ''}>`
                  : `<div class="aotm-product-card__placeholder"></div>`
              }
            </div>
            <div class="aotm-product-card__content">
              <h3 class="aotm-product-card__title">${title}</h3>
              ${price ? `<div class="aotm-product-card__price"><div class="price"><span class="price-item">${price}</span></div></div>` : ''}
            </div>
          </a>
        </div>
      `;
    }

    renderEmptyState() {
      return `
        <div class="collection collection--empty page-width" style="grid-column: 1 / -1;">
          <div class="title-wrapper center">
            <h2 class="title title--primary">No matching products found in this collection.</h2>
          </div>
        </div>
      `;
    }

    applyClientSideSoldLastOrdering(data) {
      if (!data?.sold_last) {
        return data;
      }

      const availableProducts = [];
      const soldProducts = [];

      (data.products || []).forEach((product) => {
        if (this.isHandleSold(product.handle)) {
          soldProducts.push(product);
        } else {
          availableProducts.push(product);
        }
      });

      return {
        ...data,
        products: [...availableProducts, ...soldProducts],
      };
    }

    renderGrid(products) {
      this.refreshDomReferences();
      if (!this.grid) return;

      if (!Array.isArray(products) || products.length === 0) {
        this.isApplyingProxyRender = true;
        this.grid.innerHTML = this.renderEmptyState();
        window.setTimeout(() => {
          this.isApplyingProxyRender = false;
        }, 0);
        return;
      }

      const html = products
        .map((product, index) => {
          const cachedMarkup = this.originalCardMarkupByHandle.get(product.handle);

          if (cachedMarkup) {
            const patchedMarkup = this.patchCachedCardImageMarkup(cachedMarkup, product, index);

            return `
              <div
                class="aotm-collection-grid__item"
                ${index === 0 ? `id="CollectionGridTop-${this.sectionId}"` : ''}
              >
                ${patchedMarkup}
              </div>
            `;
          }

          return this.renderFallbackCardMarkup(product, index);
        })
        .join('');

      this.isApplyingProxyRender = true;
      this.grid.innerHTML = html;
      this.hydrateProductImages();

      window.setTimeout(() => {
        this.hydrateProductImages();
        this.isApplyingProxyRender = false;
      }, 0);
    }

    buildPageUrl(pageNumber) {
      const params = this.getCurrentParams();

      if (pageNumber > 1) {
        params.set('page', String(pageNumber));
      } else {
        params.delete('page');
      }

      const queryString = params.toString();
      return queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    }

    renderPagination(data) {
      this.refreshDomReferences();

      if (this.paginationWrapper) {
        this.paginationWrapper.remove();
        this.paginationWrapper = null;
      }

      if (!data.total_pages || data.total_pages <= 1 || !this.grid) return;

      const currentPage = Number(data.current_page || 1);
      const totalPages = Number(data.total_pages || 1);

      let links = '';

      if (currentPage > 1) {
        links += `
          <li>
            <a
              href="${this.buildPageUrl(currentPage - 1)}"
              class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce"
              aria-label="Previous page"
            >
              Previous
            </a>
          </li>
        `;
      }

      for (let page = 1; page <= totalPages; page += 1) {
        if (page === currentPage) {
          links += `
            <li>
              <span
                class="pagination__item pagination__item--current"
                aria-current="page"
              >
                ${page}
              </span>
            </li>
          `;
        } else {
          links += `
            <li>
              <a
                href="${this.buildPageUrl(page)}"
                class="pagination__item link"
                aria-label="Page ${page}"
              >
                ${page}
              </a>
            </li>
          `;
        }
      }

      if (currentPage < totalPages) {
        links += `
          <li>
            <a
              href="${this.buildPageUrl(currentPage + 1)}"
              class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce"
              aria-label="Next page"
            >
              Next
            </a>
          </li>
        `;
      }

      const html = `
        <div class="pagination-wrapper">
          <nav class="pagination" role="navigation" aria-label="Pagination">
            <ul class="pagination__list list-unstyled" role="list">
              ${links}
            </ul>
          </nav>
        </div>
      `;

      this.isApplyingProxyRender = true;
      this.grid.insertAdjacentHTML('afterend', html);

      window.setTimeout(() => {
        this.isApplyingProxyRender = false;
        this.refreshDomReferences();
      }, 0);
    }

    applyResponse(data) {
      if (!data || data.ok !== true) {
        throw new Error(data && data.error ? data.error : 'Unknown response error');
      }

      const processedData = this.applyClientSideSoldLastOrdering(data);

      this.seedImageFallbacksFromProducts(processedData.products || []);
      this.updateStatusLabels(processedData);
      this.renderGrid(processedData.products || []);
      this.renderPagination(processedData);
      this.hydrateProductImages();
    }

    isSoldLastActive() {
      return Boolean(this.soldLastToggle && this.soldLastToggle.checked);
    }

    handleSearchInputChanged({ preserveSearchFocus = true } = {}) {
      if (!this.shouldRunSearch()) {
        return;
      }

      const searchNowActive = this.isSearchActive();

      if (searchNowActive && !this.wasSearchActive) {
        this.storePreSearchSort();
      }

      if (searchNowActive) {
        this.setSortToMostRelevantForSearch();
        this.wasSearchActive = true;
      } else {
        this.wasSearchActive = false;
        this.handleSearchCleared({ preserveSearchFocus });
        return;
      }

      if (this.isEnhancementActive()) {
        this.loadResults({
          resetPage: true,
          pushUrl: true,
          preserveSearchFocus,
        });
      } else {
        this.restoreOriginalState({
          updateUrl: true,
          restoreSearchFocus: preserveSearchFocus,
        });
      }
    }

    bindEvents() {
      this.root.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.matches(SEARCH_SELECTOR)) return;

        this.updateSearchClearButtonVisibility();
        this.syncFacetVisibility();
        window.clearTimeout(this.debounceTimer);

        if (target.value.trim() === '') {
          this.handleSearchCleared({ preserveSearchFocus: true });
          return;
        }

        this.debounceTimer = window.setTimeout(() => {
          this.handleSearchInputChanged({ preserveSearchFocus: true });
        }, DEBOUNCE_MS);
      });

      this.root.addEventListener('search', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.matches(SEARCH_SELECTOR)) return;

        this.updateSearchClearButtonVisibility();
        this.syncFacetVisibility();

        if (target.value.trim() === '') {
          this.handleSearchCleared({ preserveSearchFocus: true });
          return;
        }

        this.handleSearchInputChanged({ preserveSearchFocus: true });
      });

      this.root.addEventListener('keydown', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.matches(SEARCH_SELECTOR)) return;
        if (event.key !== 'Escape') return;
        if (!target.value) return;

        event.preventDefault();
        this.clearSearch({ preserveFocus: true });
      });

      this.root.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.matches(SOLD_TOGGLE_SELECTOR)) return;

        if (this.root.hasAttribute('data-aotm-search-unavailable')) return;

        if (this.isEnhancementActive()) {
          this.loadResults({ resetPage: true, pushUrl: true });
        } else {
          this.restoreOriginalState({ updateUrl: true });
        }
      });

      this.root.addEventListener('click', (event) => {
        const clearButton = event.target.closest(SEARCH_CLEAR_SELECTOR);
        if (clearButton) {
          event.preventDefault();
          this.clearSearch({ preserveFocus: true });
          return;
        }

        const paginationLink = event.target.closest('.pagination a');
        if (!paginationLink) return;
        if (!this.isEnhancementActive()) return;

        const url = new URL(paginationLink.href, window.location.origin);
        const page = url.searchParams.get('page');
        if (!page) return;

        event.preventDefault();
        this.loadResults({ page, pushUrl: true });
      });

      window.addEventListener('popstate', () => {
        this.syncControlsFromUrl();

        if (this.soldLastToggle) {
          const params = new URLSearchParams(window.location.search);
          this.soldLastToggle.checked = params.get('sold_last') === '1';
        }

        this.updateSearchClearButtonVisibility();
        this.syncFacetVisibility();

        if (this.isEnhancementActive()) {
          this.loadResults({ syncOnly: true });
        } else {
          this.restoreOriginalState();
        }
      });
    }

    async loadResults({
      page = null,
      resetPage = false,
      pushUrl = false,
      syncOnly = false,
      preserveSearchFocus = false,
    } = {}) {
      if (!this.collectionHandle) return;
      if (this.isNativeRefreshInProgress) return;

      const searchFieldState = preserveSearchFocus ? this.captureSearchFieldState() : null;
      const shouldHydrate =
        document.documentElement.getAttribute('data-aotm-collection-pending') === 'true';

      if (pushUrl) {
        this.updateBrowserUrl({ page, resetPage });
      }

      if (!this.isEnhancementActive()) {
        this.restoreOriginalState({ restoreSearchFocus: preserveSearchFocus });
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      const requestSequence = this.requestSequence + 1;
      this.requestSequence = requestSequence;
      this.activeRequestSequence = requestSequence;

      const requestUrl = this.buildRequestUrl({ page, resetPage });
      const requestedUrl = new URL(requestUrl, window.location.origin);
      const requestedQuery = requestedUrl.searchParams.get('cq') || '';
      const requestedSoldLast = requestedUrl.searchParams.get('sold_last') || '0';

      this.setLoadingState(true, { hydrating: shouldHydrate });

      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          let data = null;

          try {
            data = await response.json();
          } catch (_) {}

          if (response.status === 503 && data?.warming) {
            throw new Error(data.error || data.message || 'Collection search is warming. Please try again shortly.');
          }

          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log('AOTM v12 proxy response:', data);

        if (requestSequence !== this.activeRequestSequence) {
          return;
        }

        this.refreshDomReferences();

        const currentQuery = this.searchInput ? this.searchInput.value.trim() : '';
        const currentSoldLast = this.isSoldLastActive() ? '1' : '0';

        if (currentQuery !== requestedQuery || currentSoldLast !== requestedSoldLast) {
          return;
        }

        if (!this.isEnhancementActive()) {
          return;
        }

        this.applyResponse(data);
        this.consecutiveFailures = 0;
        this.hideSearchUnavailableNotice();
        this.clearInitialPendingState();

        if (preserveSearchFocus) {
          this.restoreSearchFieldState(searchFieldState);
        } else if (!syncOnly) {
          this.refreshDomReferences();

          const topAnchor = this.root.querySelector(
            `#CollectionGridTop-${CSS.escape(this.sectionId)}`
          );

          if (topAnchor) {
            topAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        if (requestSequence !== this.activeRequestSequence) {
          return;
        }

        console.error('[AOTM v12 collection results]', error);

        this.consecutiveFailures += 1;

        if (this.searchInput) {
          this.searchInput.value = '';
        }

        this.restorePreSearchSort();
        this.updateSearchClearButtonVisibility();
        this.syncFacetVisibility();

        this.showSearchUnavailableNotice(
          error.message && error.message.includes('warming')
            ? 'Collection search is warming. Please try again in a moment.'
            : 'Collection search is temporarily unavailable. Please use the filters or browse the collection.'
        );

        if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          this.disableEnhancementControls();
        }

        this.clearInitialPendingState();
      } finally {
        if (requestSequence === this.activeRequestSequence) {
          this.setLoadingState(false);
          this.hydrateProductImages();
        }
      }
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!root.dataset.aotmCollectionControllerReady) {
        new AotmCollectionResultsController(root);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', init);
})();
